import {
  useState, useEffect, ChangeEvent,
} from 'react';
import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import { Alert, Avatar, Badge } from '@mui/material';
import { useApi } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { FILE_SIZE_LIMIT } from '../lib/constants';

interface Props {
  avatarUrl: string;
  username: string;
  userId?: number;
  /** Used to skip the save and auto upload the new avatar */
  onUpload?: (fileData: FormData, filename: string) => void;
}

export const AvatarImageUpload = ({
  avatarUrl, username, userId, onUpload,
}: Props) => {
  const [hasChangedFile, setHasChangedFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [preview, setPreview] = useState<string | undefined>(avatarUrl);
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const API = useApi();

  // Update preview when avatarUrl prop changes (only if user hasn't made changes)
  useEffect(() => {
    if (!hasChangedFile) {
      setPreview(avatarUrl);
    }
  }, [avatarUrl, hasChangedFile]);

  // changing return values totally broke this! Thinking further on solutions
  useEffect(() => {
    if (!hasChangedFile) {
      return undefined;
    }
    if (!selectedFile) {
      setPreview(undefined);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);
    if (onUpload) {
      const fileData = new FormData();
      fileData.append('file', selectedFile as any);
      onUpload(fileData, selectedFile?.name as string);
    }

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile, hasChangedFile]);

  const onSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
    setHasChangedFile(true);
    if (!e.currentTarget.files || e.currentTarget.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }
    const file = e.currentTarget.files[0];

    if (file.size > FILE_SIZE_LIMIT) {
      setIsFileTooLarge(true);
      return;
    }
    setIsFileTooLarge(false);
    setSelectedFile(file);
  };

  const saveImage = async () => {
    try {
      setIsSaving(true);
      const fileData = new FormData();
      fileData.append('file', selectedFile as any);
      if (userId) {
        await API.updateAvatar(userId, fileData, selectedFile?.name as string);
      }
      enqueueSnackbar('The avatar has been updated!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Error saving avatar ${err}`, { variant: 'error' });
    }
    setIsSaving(false);
    setIsFileTooLarge(false);
    setHasChangedFile(false);
  };

  return (
    <div className="w-full text-center">
      <label className="relative hover:opacity-80 cursor-pointer" htmlFor="upload" title="Edit profile picture">
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          badgeContent={
            <CameraAltRounded fontSize="large" color="secondary" className="rounded border bg-white" />
          }
        >
          <Avatar
            sx={{ width: 160, height: 160 }}
            alt={username}
            src={preview}
          />
        </Badge>
        <input
          onChange={onSelectFile}
          type="file"
          name="file"
          id="upload"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
        />
        {!!hasChangedFile && !isFileTooLarge && userId
          && (
            <div>
              <button disabled={isSaving} className="btn-primary-outlined text-xs mt-2" type="button" onClick={saveImage}>
                {isSaving ? 'Saving...' : 'Save Image'}
              </button>
            </div>
          )}
      </label>
      {isFileTooLarge && (
        <Alert severity="error" variant="filled" className="mt-2 mx-auto max-w-[200px] text-center">
          Make sure to keep images under 2mb!
        </Alert>
      )}
    </div>
  );
};
